/**
 * Multi Image Input
 * ====================
 * 
 * JS events fired on the [data-multi-image-input]:
 * 
 *  - mii-change (any change)
 *  - mii-file-meta-change
 *  - mii-file-delete
 *  - mii-file-add
 * 
 * Will work with any HTML element with the appropriate data attributes on its
 * descendents. Here is a (possibly incomplete) list:
 * 
 * Outer container data attributes:
 *  - data-multi-image-input (input name - added as inputName meta value to each file item)
 *  - data-max-files (int on outer container, optional, any value <= 0 (or no value) means no limit)
 *  - data-max-file-size (int, bytes, on outer container, optional)
 * 
 * Descendent element data attributes:
 *  - data-rows (contains image file rows, required)
 *  - data-row-template (<script> containing row template, required)
 *  - data-add (clickable for adding a new file, required)
 *
 * For inside data-row-template: 
 *  - data-file-meta (input or div/span, optional, attr value is the meta key)
 *  - data-thumb (container for <img>, optional, must be empty)
 *  - data-delete (clickable for removing a row, required)
 * 
 */

import { cloneDeep, each, filter, isNumber, isString, map, max } from "lodash";

const errorModal: Function = window["iw"].modal.errorModal;
const fileToDataUri: Function = window["iw"].utils.fileToDataUri;
const getResizedImageSrcAsDataUri: Function = window["iw"].utils.getResizedImageSrcAsDataUri;
const safeParseJSON: Function = window["iw"].utils.safeParseJSON;

const allowedTypes: string[] = ["image/jpeg", "image/png", "image/gif"];
const allowedTypesString: string = "JPEG, PNG, GIF";

declare const jQuery: any;
const $ = jQuery;

export interface MultiImageInputItem
{
    file: File;
    input: HTMLInputElement;
    meta: { [metaKey: string]: any };
}

export interface FileItem
{
    file: File;
    meta: { [metaKey: string]: any };
}

export interface DefaultRowItem
{
    id: number;
    url: string;
    meta: { [metaKey: string]: any };
    deleted: boolean;
}

export interface MiiCtrls
{
    getFileItems(): FileItem[];
    getDefaultItems(): DefaultRowItem[];
    getMinFiles(): number;
    minFilesReached(): boolean;
    updateView(): Promise<void>;
    deleteAllItems(): void;
    reset(): void;
}

export interface MiiCtrlsByName
{
    [inputName: string]: MiiCtrls;
}

export function initMultiImageInputs($wrap: any = undefined): MiiCtrlsByName
{
    if (!$wrap)
    {
        $wrap = $(document.body);
    }
    const $miiWraps = $wrap.find(`[data-multi-image-input]`);
    const miiCtrlsByName: MiiCtrlsByName = {};
    if ($miiWraps.length === 0)
    {
        return miiCtrlsByName;
    }
    $miiWraps.each(function () {
        const $miiWrap = $(this);
        const inputName: string = $miiWrap.attr("data-multi-image-input");
        if (!inputName)
        {
            console.warn(`[data-multi-image-input] lacks name`);
            return;
        }
        miiCtrlsByName[inputName] = initMultiImageInput($miiWrap);
    });
    return miiCtrlsByName;
}

// $miiWrap - JQuery for [data-multi-image-input]
// This func takes care of the UI contained within the [data-multi-image-input].
// Keeps the files & their inputs stored internally.
// Returns object with methods getFiles(), updateView(), etc..
export function initMultiImageInput($miiWrap): MiiCtrls
{
    const inputName: string = $miiWrap.attr("data-multi-image-input") || "";
    const maxNumFiles: number = parseInt($miiWrap.attr("data-max-files"), 10) || -1;
    const minNumFiles: number = parseInt($miiWrap.attr("data-min-files"), 10) || -1;
    const maxFileSize: number = parseInt($miiWrap.attr("data-max-file-size"), 10) || 0; // bytes
    const maxFileSizeDisplay: string = $miiWrap.attr("data-max-file-size-display") || "";

    const maxItemsReachedMessage: string = (maxNumFiles === 1) ?
        `Only one image is allowed` :
        `There is a maximum of ${maxNumFiles} images allowed`;

    const rowTemplateHtml: string = $.trim($miiWrap.find("[data-row-template]").html() || "");
    const thumbWidth: number = parseInt($miiWrap.find("[data-thumb-width]").attr("data-thumb-width"), 10);
    const thumbHeight: number = parseInt($miiWrap.find("[data-thumb-height]").attr("data-thumb-height"), 10);
    
    const $defaultRowsWrap = $miiWrap.find("[data-default-rows]");
    const $rowsWrap = $miiWrap.find("[data-rows]");
    
    const defaultItems: DefaultRowItem[] = safeParseJSON($miiWrap.attr("data-default-items") || "[]") || [];
    const items: MultiImageInputItem[] = [];

    const fileTypeIsOk = (file: File): boolean => {
        return allowedTypes.indexOf(file.type) >= 0;
    };

    const getUndeletedDefaultItems = (): DefaultRowItem[] => filter(defaultItems, (item: DefaultRowItem) => !item.deleted);
    
    const getTotalNumItems = (): number => items.length + getUndeletedDefaultItems().length;
    const maxItemsReached = (): boolean => ((maxNumFiles > 0) && (getTotalNumItems() >= maxNumFiles));
    const numRemainingImages = (): number => {
        if (maxNumFiles <= 0)
        {
            return -1;
        }
        return max([0, maxNumFiles - getTotalNumItems()]);
    };
    const numNewImagesIsOk = (numImages: number): boolean => {
        const maxNum: number = numRemainingImages();
        return (maxNum < 0) || (numImages <= maxNum);
    };
    const updateWrapClass = (): void => {
        if (getTotalNumItems() === 0)
        {
            $miiWrap.addClass("mii-empty");
        }
        else
        {
            $miiWrap.removeClass("mii-empty");
        }
        if (maxItemsReached())
        {
            $miiWrap.addClass("mii-max-reached");
        }
        else
        {
            $miiWrap.removeClass("mii-max-reached");
        }
    };
    const renderRows = async (): Promise<void> => {
        $rowsWrap.empty();
        const numItems: number = items.length;
        for (let itemIndex = 0; itemIndex < numItems; ++itemIndex)
        {
            const item: MultiImageInputItem = items[itemIndex];
            const updateMeta = updateItemMeta.bind(null, itemIndex);
            const deleteItem = deleteItemAtIndex.bind(null, itemIndex);
            $rowsWrap.append(await itemToRow(item, rowTemplateHtml, updateMeta, deleteItem, thumbWidth, thumbHeight));
        }
    };
    const renderDefaultRows = (): void => {
        $defaultRowsWrap.empty();
        const numItems: number = defaultItems.length; // both deleted & undeleted - this is necessary
        for (let itemIndex = 0; itemIndex < numItems; ++itemIndex)
        {
            const defaultItem: DefaultRowItem = defaultItems[itemIndex];
            const updateMeta = updateDefaultItemMeta.bind(null, itemIndex);
            const deleteItem = deleteDefaultItemAtIndex.bind(null, itemIndex);
            const undoDeleteItem = undoDeleteDefaultItemAtIndex.bind(null, itemIndex);
            $defaultRowsWrap.append(itemToDefaultRow(defaultItem, rowTemplateHtml, updateMeta, deleteItem, undoDeleteItem, thumbWidth, thumbHeight));
        }
    };
    const updateView = async (): Promise<void> => {
        updateWrapClass();
        renderDefaultRows();
        await renderRows();
        $miiWrap.trigger("mii-view-updated");
    };
    const updateItemMeta = (itemIndex: number, metaKey: string, value: any): void => {
        if (!items[itemIndex])
        {
            throw new Error(`updateItemMeta passed bad index ${itemIndex}`);
        }
        items[itemIndex].meta[metaKey] = value;
        $miiWrap.trigger("mii-file-meta-change").trigger("mii-change");
    };
    const updateDefaultItemMeta = (itemIndex: number, metaKey: string, value: any): void => {
        if (!defaultItems[itemIndex])
        {
            throw new Error(`updateDefaultItemMeta passed bad index ${itemIndex}`);
        }
        defaultItems[itemIndex].meta[metaKey] = value;
        $miiWrap.trigger("mii-file-meta-change").trigger("mii-change");
    };
    const deleteItemAtIndex = (itemIndex: number): void => {
        if (!items[itemIndex])
        {
            throw new Error(`deleteItemAtIndex passed bad index ${itemIndex}`);
        }
        $(items[itemIndex].input).parent().remove(); // keep dom tidy
        items.splice(itemIndex, 1);
        updateView();
        $miiWrap.trigger("mii-file-delete").trigger("mii-change");
    };
    const deleteDefaultItemAtIndex = (itemIndex: number): void => {
        if (!defaultItems[itemIndex])
        {
            throw new Error(`deleteDefaultItemAtIndex passed bad index ${itemIndex}`);
        }
        defaultItems[itemIndex].deleted = true;
        updateView();
        $miiWrap.trigger("mii-file-delete").trigger("mii-change");
    };
    const undoDeleteDefaultItemAtIndex = (itemIndex: number): void => {
        if (!defaultItems[itemIndex])
        {
            throw new Error(`undoDeleteDefaultItemAtIndex passed bad index ${itemIndex}`);
        }
        if (maxItemsReached())
        {
            errorModal(maxItemsReachedMessage);
            return;
        }
        defaultItems[itemIndex].deleted = false;
        updateView();
        $miiWrap.trigger("mii-file-undo-delete").trigger("mii-change");
    };
    const undoAllDefaultItemDeletes = (): void => {
        each(defaultItems, (defaultItem: DefaultRowItem) => {
            if (maxItemsReached())
            {
                errorModal(maxItemsReachedMessage);
                return false;
            }
            defaultItem.deleted = false;
        });
        updateView();
        $miiWrap.trigger("mii-change");
    };
    const deleteAllItems = (): void => {
        if (items.length === 0)
        {
            return;
        }
        each(items, (item) => {
            $(item.input).parent().remove(); // keep dom tidy
        });
        items.length = 0;
        updateView();
        $miiWrap.trigger("mii-file-delete").trigger("mii-change");
    };
    const addItem = (): void => {
        if (maxItemsReached())
        {
            errorModal(maxItemsReachedMessage);
            return;
        }
        const $fileInput = $(`<input type="file" ${(maxNumFiles === 1) ? "" : 'multiple="multiple" '}/>`);
        const $fileInputWrap = $(`<div style="display:none!important"></div>`).appendTo($miiWrap).append($fileInput);
        $fileInput.on("change", function onFileInputChange(event) {
            const fileInput: HTMLInputElement = $fileInput[0];
            if (fileInput.files.length === 0)
            {
                // user aborts image add
                $fileInputWrap.remove();
                return;
            }
            if (!numNewImagesIsOk(fileInput.files.length))
            {
                // system prohibits image add
                errorModal(maxItemsReachedMessage);
                $fileInputWrap.remove(); // FIXME - should this be here?
                return;
            }
            const newItems: MultiImageInputItem[] = [];
            each(fileInput.files, (file: File) => {
                if (maxFileSize >= 0 && file.size > maxFileSize)
                {
                    errorModal(`File "${file.name}" is too large!` + (maxFileSizeDisplay ? ` Maximum size is ${maxFileSizeDisplay}.` : ``));
                    $fileInputWrap.remove();
                    newItems.length = 0;
                    return false;
                }
                if (!fileTypeIsOk(file))
                {
                    errorModal(`File "${file.name}" has an invalid file type. Allowed file types are: ` + allowedTypesString);
                    $fileInputWrap.remove();
                    newItems.length = 0;
                    return false;
                }
                newItems.push({
                    file,
                    input: fileInput,
                    meta: {
                        inputName
                    }
                });
            });
            if (newItems.length > 0)
            {
                each(newItems, (item: MultiImageInputItem) => { items.push(item); });
                updateView();
                $miiWrap.trigger("mii-file-add").trigger("mii-change");
            }
        }).trigger("click");
    };
    $miiWrap.find("[data-add]").on("click", function (event) {
        addItem();
    });
    const init = async () => {
        await updateView();
        $miiWrap.trigger("mii-init");
    };
    init();
    return {
        getFileItems(): FileItem[] {
            return map(items, (item: MultiImageInputItem) => {
                return { file: item.file, meta: cloneDeep(item.meta) };
            });
        },

        getDefaultItems(): DefaultRowItem[] {
            return defaultItems;
        },

        reset(): void {
            // Note - deleteAllItems must execute before undoAllDefaultItemDeletes:
            deleteAllItems();
            undoAllDefaultItemDeletes();
        },

        // returns positive int or -1 if no minimum
        getMinFiles(): number {
            return minNumFiles;
        },
        
        minFilesReached(): boolean {
            return (minNumFiles > 0) ? (getTotalNumItems() >= minNumFiles) : true;
        },

        updateView(): Promise<void> {
            return updateView();
        },

        // Deletes newly added items only, not default items
        deleteAllItems(): void {
            deleteAllItems();
        }
    };
}

function itemToDefaultRow(
    item: DefaultRowItem,
    rowTemplateHtml: string,
    updateMeta: (metaKey: string, value: any) => void,
    deleteItem: () => void,
    undoDeleteItem: () => void,
    thumbWidth: number,
    thumbHeight: number
) {
    if (item.deleted)
    {
        const $title = $(`<span class="mii-deleted-title">`).text(item.meta.title || "Image");
        const $undo = $(`<span class="mii-undo-delete">Undo</span>`);
        const $row = $(`<div class="mii-deleted-default-item-row">Deleted </div>`).append($title).append($undo);
        $undo.on("click", function (event) {
            undoDeleteItem();
            return false;
        });
        return $row;
    }
    const $row = $(rowTemplateHtml);
    thumbWidth = parseInt($row.find("[data-thumb-width]").attr("data-thumb-width"), 10) || thumbWidth;
    thumbHeight = parseInt($row.find("[data-thumb-height]").attr("data-thumb-height"), 10) || thumbHeight;
    const $img = $(`<img class="mii-thumbnail">`).attr("src", item.url);
    $row.find("[data-thumb]").append($img);
    addMetaRowsToItemRow(item, $row, updateMeta);
    addDeleteButtonToItemRow($row, deleteItem);
    return $row;
}

async function itemToRow(
    item: MultiImageInputItem,
    rowTemplateHtml: string,
    updateMeta: (metaKey: string, value: any) => void,
    deleteItem: () => void,
    thumbWidth: number,
    thumbHeight: number)
{
    const $row = $(rowTemplateHtml);
    thumbWidth = parseInt($row.find("[data-thumb-width]").attr("data-thumb-width"), 10) || thumbWidth;
    thumbHeight = parseInt($row.find("[data-thumb-height]").attr("data-thumb-height"), 10) || thumbHeight;
    const $img = await createThumbnailFromFile(item.file, thumbWidth, thumbHeight);
    if ($img)
    {
        $row.find("[data-thumb]").append($img);
    }
    addMetaRowsToItemRow(item, $row, updateMeta);
    addDeleteButtonToItemRow($row, deleteItem);
    return $row;
}

function addDeleteButtonToItemRow($row, deleteItem: () => void): void {
    $row.find(`[data-delete]`).on("click", function (event) {
        deleteItem();
        return false;
    });
}

function addMetaRowsToItemRow(
    item: DefaultRowItem|MultiImageInputItem,
    $row,
    updateMeta: (metaKey: string, value: any) => void): void
{
    $row.find("[data-file-meta]").each(function () {
        const $meta = $(this);
        const metaKey: string = $meta.attr("data-file-meta");
        const value: any = item.meta?.[metaKey];
        if (!isString(value) && !isNumber(value) && (value !== undefined) && (value !== null))
        {
            return;
        }
        if ($meta.is(":input"))
        {
            $meta.val(value || "").on("change", function (event) {
                updateMeta(metaKey, $meta.val());
            });
        }
        else
        {
            $meta.text(value);
        }
    });
}

// Creates a probably-unique string identifier for a File
function fileToString(file: File): string
{
    return `${file.name}%${file.size}`;
}

const thumbDataUriCache: { [fileString: string]: string } = {}; // filestring  => dataUri

// Returns JQuery
async function createThumbnailFromFile(file: File, width: number, height: number): Promise<any>
{
    const fs: string = fileToString(file);
    let src: string;
    if (thumbDataUriCache[fs])
    {
        src = thumbDataUriCache[fs];
    }
    else
    {
        try
        {
            let dataUri: string = await fileToDataUri(file);
            if (!dataUri)
            {
                throw new Error(`No dataUri created by fileToDataUri`);
            }
            if (width && height)
            {
                dataUri = await getResizedImageSrcAsDataUri(dataUri, width, height);
            }
            src = thumbDataUriCache[fs] = dataUri;
        }
        catch (error) 
        {
            return $(`<div class="mii-failed-image">`);
        }
    }
    const $img = $(`<img class="mii-thumbnail" />`);
    $img.attr("src", src);
    return $img;
}
